import { useState, useRef } from "react";
import { UploadCloud, Image as ImageIcon, X, Link as LinkIcon, Check } from "lucide-react";

export default function DragDropImageUpload({ label, value, onChange, placeholder = "Drag & drop image here, or click to upload" }) {
  const [isDragging, setIsDragging] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [tempUrl, setTempUrl] = useState("");
  const fileInputRef = useRef(null);

  const handleFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      onChange(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const applyUrl = () => {
    if (tempUrl.trim()) {
      onChange(tempUrl.trim());
      setShowUrlInput(false);
      setTempUrl("");
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex items-center justify-between">
          <label className="block text-[10px] font-mono uppercase text-slate-400 font-semibold">{label}</label>
          <button
            type="button"
            onClick={() => setShowUrlInput(!showUrlInput)}
            className="text-[10px] font-mono text-primary hover:underline flex items-center gap-1"
          >
            <LinkIcon className="w-3 h-3" /> {showUrlInput ? "File Drop Mode" : "Paste Image URL"}
          </button>
        </div>
      )}

      {showUrlInput ? (
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="https://images.unsplash.com/..."
            value={tempUrl}
            onChange={(e) => setTempUrl(e.target.value)}
            className="flex-1 h-11 px-3 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white font-mono"
          />
          <button
            type="button"
            onClick={applyUrl}
            className="h-11 px-4 rounded-xl bg-primary text-white text-xs font-semibold flex items-center gap-1"
          >
            <Check className="w-4 h-4" /> Apply
          </button>
        </div>
      ) : value ? (
        <div className="relative group rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 h-36 flex items-center justify-center">
          <img src={value} alt="Preview" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-semibold border border-slate-700"
            >
              Replace Photo
            </button>
            <button
              type="button"
              onClick={() => onChange("")}
              className="p-1.5 rounded-xl bg-red-500 text-white text-xs"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all flex flex-col items-center justify-center gap-2 ${
            isDragging
              ? "border-primary bg-primary/10 text-primary"
              : "border-slate-800 bg-slate-950/60 text-slate-400 hover:border-slate-700 hover:text-slate-300"
          }`}
        >
          <div className="w-10 h-10 rounded-2xl bg-slate-900 grid place-items-center text-primary">
            <UploadCloud className="w-5 h-5" />
          </div>
          <div className="text-xs font-semibold">{placeholder}</div>
          <div className="text-[10px] text-slate-500 font-mono">Supports PNG, JPG, WEBP or Drag & Drop</div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}
